/**
 * Analytics Layout Component
 *
 * Shared layout for all analytics pages with sub-navigation.
 */
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../ui/button';

/**
 * Analytics navigation tabs
 */
const ANALYTICS_TABS = [
  { path: '/analytics', label: 'Dashboard', icon: '📊' },
  { path: '/analytics/executions', label: 'Executions', icon: '🚀' },
  { path: '/analytics/costs', label: 'Costs', icon: '💰' },
  // Admin-only: System reports live connection counts and infrastructure shape,
  // and its route is AdminRoute — showing the tab to everyone would just bounce
  // them to /dashboard. The route guard is the real gate; this is only the UI.
  { path: '/analytics/system', label: 'System', icon: '🖥️', adminOnly: true },
];

/**
 * Analytics Sub Navigation Component
 */
export function AnalyticsSubNav() {
  const location = useLocation();
  const currentPath = location.pathname;
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const tabs = ANALYTICS_TABS.filter((t) => !t.adminOnly || isAdmin);

  return (
    <nav className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
      {/* Analytics renders no app header, so without this the only way back to
          the rest of the app is the browser back button. This sub-nav is the one
          piece every analytics page actually renders (AnalyticsLayout below is
          unused), so it is the only reliable place to put it. */}
      <Button variant="ghost" size="sm" className="gap-1.5" asChild>
        <Link to="/dashboard" title="Back to app"><span aria-hidden="true">←</span>
          {/* Deliberately NOT "Back to Dashboard": the tab beside it is already
              called Dashboard (the analytics overview) and points somewhere
              else. Two links named Dashboard in one nav is a coin toss. */}
          <span className="sr-only">Back to app</span></Link>
      </Button>
      <span className="w-px h-5 bg-slate-300 mx-0.5" aria-hidden="true" />
      {tabs.map((tab) => {
        const isActive = currentPath === tab.path;
        return (
          <Button
              variant={isActive ? 'default' : 'ghost'}
              size="sm"
              className="gap-1.5" asChild>
            <Link key={tab.path} to={tab.path}><span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span></Link>
          </Button>
        );
      })}
    </nav>
  );
}

/**
 * Analytics Header Component
 */
interface AnalyticsHeaderProps {
  title: string;
  description: string;
  children?: React.ReactNode;
}

export function AnalyticsHeader({ title, description, children }: AnalyticsHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-3xl font-bold">{title}</h1>
        <p className="text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center gap-4">
        {children}
        <AnalyticsSubNav />
      </div>
    </div>
  );
}

/**
 * Analytics Layout Props
 */
interface AnalyticsLayoutProps {
  title: string;
  description: string;
  headerContent?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Analytics Layout Component
 */
export function AnalyticsLayout({
  title,
  description,
  headerContent,
  children,
}: AnalyticsLayoutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-7xl mx-auto py-8">
        <AnalyticsHeader title={title} description={description}>
          {headerContent}
        </AnalyticsHeader>
        {children}
      </div>
    </div>
  );
}

export default AnalyticsLayout;
